import { useMutation } from '@apollo/client';
import { CREATE_SAFETY_SIGNATURES, UPDATE_SAFETY_SIGNATURES } from 'apollo/mutations/safety-signature-mutations';
import { DateTime } from 'luxon';
import ISafetySignature, { ISafetySignatureCreateInput, ISafetySignatureUpdateInput } from 'types/SafetySignature';
import {
  dateTimeFromISOKeepZone,
  getNowIsoAtUtcWithLocalTimeZone,
  isoTimeStampLocal,
  offsetInSeconds,
} from 'utils/dateUtils';
import { t } from 'utils/localize';
import uuid from 'uuid';
import useActiveMember from '../../store/useActiveMember';

export default function useSafetySignature() {
  const [createSafetySignatureMutation] = useMutation(CREATE_SAFETY_SIGNATURES);
  const [updateSafetySignatureMutation] = useMutation(UPDATE_SAFETY_SIGNATURES);
  const activeMember = useActiveMember();

  function createSafetySignature(
    startTime: DateTime,
    startTimeDST: boolean,
    endTime: DateTime,
    endTimeDST: boolean,
    memberId: string,
    injured?: boolean,
    injuryDescription?: string,
    timeAccurate?: boolean,
    timeAccurateReason?: string,
    breakPolicyFollowed?: boolean,
    breakPolicyFollowedReason?: string
  ) {
    const now = DateTime.local();
    const start = dateTimeFromISOKeepZone(startTime.toISO()).startOf('day').set({ second: 0, millisecond: 0 });
    const end = dateTimeFromISOKeepZone(endTime.toISO()).set({ second: 0, millisecond: 0 });

    const entry: ISafetySignatureCreateInput = {
      id: uuid(),
      memberId,
      proxyMemberId: activeMember.id!,
      startTime: start.toISO(),
      startTimeDst: startTimeDST,
      endTime: end.toISO(),
      endTimeDst: endTimeDST,
      deviceType: t('Web Browser'),
      injured,
      injuryDescription,
      timeAccurate,
      timeAccurateReason,
      breakPolicyFollowed,
      breakPolicyFollowedReason,
      createdOnDst: now.isInDST,
      createdOnOffset: offsetInSeconds(now),
      createdOn: getNowIsoAtUtcWithLocalTimeZone(),
    };

    return createSafetySignatureMutation({ variables: { safetySignature: entry } });
  }

  function resolveSafetySignature(
    signature: ISafetySignature,
    injuredResolvedBy?: string,
    injuredResolvedComment?: string,
    timeAccurateResolvedBy?: string,
    timeAccurateResolvedComment?: string,
    breakPolicyFollowedResolvedBy?: string,
    breakPolicyFollowedResolvedComment?: string
  ) {
    const entry: ISafetySignatureUpdateInput = {
      id: signature.id,
    };

    if (injuredResolvedBy) {
      entry.injuredResolvedBy = injuredResolvedBy;
      entry.injuredResolvedComment = injuredResolvedComment;
      entry.injuredResolvedOn = isoTimeStampLocal();
    }

    if (timeAccurateResolvedBy) {
      entry.timeAccurateResolvedBy = timeAccurateResolvedBy;
      entry.timeAccurateResolvedComment = timeAccurateResolvedComment;
      entry.timeAccurateResolvedOn = isoTimeStampLocal();
    }

    if (breakPolicyFollowedResolvedBy) {
      entry.breakPolicyFollowedResolvedBy = breakPolicyFollowedResolvedBy;
      entry.breakPolicyFollowedResolvedComment = breakPolicyFollowedResolvedComment;
      entry.breakPolicyFollowedResolvedOn = isoTimeStampLocal();
    }

    return updateSafetySignatureMutation({ variables: { safetySignature: entry } });
  }

  return { createSafetySignature, resolveSafetySignature };
}
